import React, { useState, useRef, useEffect } from "react";
import "../../App.css";
import { useApp } from '../../core/context';
import { InlineIcon } from '../../components/Icon';
import { OrgBrandName } from '../../components/OrgBrand';

function OrgSettings() {
  const { org, setOrg, showToast, setView, user } = useApp();
  const [form, setForm] = useState({ ...org });
  const [saving, setSaving] = useState(false);
  const logoRef = useRef(null);
  const sigRef = useRef(null);

  useEffect(() => {
    setForm({ ...org });
  }, [org]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.name?.trim()) {
      showToast("Legal / Firm Name is required", "error");
      return false;
    }
    if (!form.email?.trim()) {
      showToast("Email Address is required", "error");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      showToast("Enter a valid email address", "error");
      return false;
    }
    return true;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await setOrg({ ...org, ...form });
      showToast("Organisation details saved!", "success");
    } catch (e) {
      showToast(e.message || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const patchOrg = async (patch) => {
    const next = { ...org, ...form, ...patch };
    setForm(next);
    try {
      await setOrg(next);
      return true;
    } catch (e) {
      showToast(e.message || "Could not save", "error");
      return false;
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { showToast("Logo must be under 2MB", "error"); return; }
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const url = ev.target.result;
      set("logoUrl", url);
      const ok = await patchOrg({ logoUrl: url });
      if (ok) showToast("Logo uploaded and saved!", "success");
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = async () => {
    set("logoUrl", "");
    const ok = await patchOrg({ logoUrl: "" });
    if (ok) showToast("Logo removed", "info");
  };

  const handleSignatureUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 1 * 1024 * 1024) { showToast("Signature must be under 1MB", "error"); return; }
    const reader = new FileReader();
    reader.onload = async (ev) => {
      set("signature", ev.target.result);
      showToast("Signature added — click Save Changes to persist", "info");
    };
    reader.readAsDataURL(file);
  };

  return (
    <div style={{ maxWidth: 720 }}>
      <div className="card card-gold" style={{ marginBottom: 20 }}>
        <div className="card-head"><div className="card-title">Organisation Logo</div></div>
        <div className="card-body">
          <div style={{ display:"flex", alignItems:"center", gap:24 }}>
            <div style={{ width:100,height:100,borderRadius:"50%",border:"2px dashed var(--border)",display:"flex",alignItems:"center",justifyContent:"center",background:"var(--navy)",flexShrink:0,overflow:"hidden",padding: form.logoUrl ? 0 : 8 }}>
              {form.logoUrl
                ? <img src={form.logoUrl} alt={form.name || "Logo"} style={{ width:"100%",height:"100%",objectFit:"cover" }} />
                : <div style={{ textAlign:"center", color:"#fff", transform:"scale(0.85)" }}>
                    <OrgBrandName name={form.name} className="sb-brand" />
                  </div>
              }
            </div>
            <div>
              <div style={{ fontSize:13,fontWeight:600,color:"var(--navy)",marginBottom:6 }}>Upload your logo</div>
              <div style={{ fontSize:12,color:"var(--muted)",marginBottom:12 }}>PNG or JPG, max 2MB. Appears in the sidebar and on invoices.<br/>Legal / Firm Name above controls the text shown when no logo is uploaded.</div>
              <input type="file" ref={logoRef} accept="image/*" style={{ display:"none" }} onChange={handleLogoUpload} />
              <div style={{ display:"flex",gap:10 }}>
                <button type="button" className="btn btn-gold btn-sm" onClick={()=>logoRef.current?.click()}><InlineIcon name="upload" size={13} gap={4}>Upload Logo</InlineIcon></button>
                {form.logoUrl && <button type="button" className="btn btn-sm btn-red" onClick={removeLogo}><InlineIcon name="close" size={13} gap={4}>Remove</InlineIcon></button>}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head"><div className="card-title">Firm Details</div></div>
        <div className="card-body">
          <div className="form-grid-2">
            {[
              ["name","Legal / Firm Name", true],
              ["email","Email Address", true],
              ["phone","Phone Number", false],
              ["gstin","GSTIN", false],
              ["pan","PAN", false],
              ["sac","Default SAC Code", false],
              ["bankName","Bank Name", false],
              ["accountNo","Account Number", false],
              ["ifsc","IFSC Code", false],
              ["upi","UPI ID", false],
            ].map(([k,l,req]) => (
              <div key={k} className="f-group">
                <label className="f-label">{l}{req ? " *" : ""}</label>
                <input className="f-input" value={form[k]||""} onChange={e=>set(k,e.target.value)} />
              </div>
            ))}
          </div>
          <div className="f-group" style={{ marginTop:16 }}>
            <label className="f-label">Address</label>
            <textarea className="f-textarea" value={form.address||""} onChange={e=>set("address",e.target.value)} rows={2} />
          </div>
          <div className="form-grid-2" style={{ marginTop:16 }}>
            <div className="f-group">
              <label className="f-label">GST Rate (%)</label>
              <input className="f-input" type="number" min="0" max="100" value={form.gstRate ?? 18} onChange={e=>set("gstRate",Number(e.target.value))} />
            </div>
            <div className="f-group">
              <label className="f-label">Firm State (CGST/SGST vs IGST)</label>
              <select className="f-select" value={form.state||"Karnataka"} onChange={e=>set("state",e.target.value)}>
                {["Andhra Pradesh","Bihar","Chhattisgarh","Delhi","Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Odisha","Punjab","Rajasthan","Tamil Nadu","Telangana","Uttar Pradesh","Uttarakhand","West Bengal"].map(s=><option key={s}>{s}</option>)}
              </select>
              <div className="f-hint">Same state as client → CGST+SGST. Different → IGST.</div>
            </div>
          </div>
          <div className="f-group" style={{ marginTop:16 }}>
            <label className="f-label">Invoice Terms & Conditions</label>
            <textarea className="f-textarea" rows={4} value={form.terms||""} onChange={e=>set("terms",e.target.value)} placeholder="1. Payment due within 15 days."/>
          </div>
          <div className="f-group" style={{ marginTop:16 }}>
            <label className="f-label">Authorised Signature</label>
            <div style={{padding:10,background:"var(--cream)",borderRadius:8,border:"1px solid var(--border)",marginBottom:8,minHeight:50,display:"flex",alignItems:"center"}}>
              {form.signature ? <img src={form.signature} alt="Signature" style={{maxHeight:55,maxWidth:180,objectFit:"contain"}}/> : <span style={{fontSize:12,color:"var(--muted)"}}>No signature uploaded</span>}
            </div>
            <input type="file" ref={sigRef} accept="image/*" style={{display:"none"}} onChange={handleSignatureUpload}/>
            <button type="button" className="btn btn-sm" onClick={()=>sigRef.current?.click()}><InlineIcon name="upload" size={13} gap={4}>Upload Signature</InlineIcon></button>
            {form.signature && <button type="button" className="btn btn-sm btn-red" style={{marginLeft:8}} onClick={()=>set("signature","")}><InlineIcon name="close" size={13} gap={4}>Remove</InlineIcon></button>}
          </div>
        </div>
        <div style={{ padding:"0 20px 20px" }}>
          <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save Changes"}</button>
        </div>
      </div>
      {(user?.role === 'admin' || user?.role === 'manager') && (
        <div className="card" style={{ marginTop: 20 }}>
          <div className="card-head"><div className="card-title">Integrations</div></div>
          <div className="card-body" style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            <button type="button" className="btn" onClick={() => setView('settings-kraya')}>
              <InlineIcon name="whatsapp" size={14} gap={6}>WhatsApp (Kraya)</InlineIcon>
            </button>
            <button type="button" className="btn" onClick={() => setView('settings-zoho')}>
              <InlineIcon name="upload" size={14} gap={6}>Zoho WorkDrive</InlineIcon>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export { OrgSettings };
